'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import { useToggleSeedSown } from '@api/seedTracking';
import {
  createHandleToggleSown,
  generateSeedRequirements,
  type Garden,
  type Plant,
  type SeedRequirement,
} from '@modules/dashboard/business_logic';
import { ComponentType } from 'react';

export interface SeedWidgetProps {
  selectedGarden: Garden | null;
  seedRequirements: SeedRequirement[];
  onToggleSown: (plantId: string, currentStatus: boolean) => void;
}

interface SeedWidgetContainerProps {
  View: ComponentType<SeedWidgetProps>;
  selectedGarden: Garden | null;
  plants: Plant[];
}

export function SeedWidgetContainer({ View, selectedGarden, plants }: SeedWidgetContainerProps) {
  const toggleSeedSown = useToggleSeedSown();

  const seedTracking =
    useQuery(
      api.seedTracking.queries.getSeedTracking,
      selectedGarden ? { gardenId: selectedGarden._id } : 'skip'
    ) ?? [];

  const sowingConfigs =
    useQuery(
      api.plantSowingConfig.queries.getSowingConfigsByGarden,
      selectedGarden ? { gardenId: selectedGarden._id } : 'skip'
    ) ?? [];

  const plantDefaults = useQuery(api.userPlantDefaults.queries.getUserPlantDefaults, {}) ?? [];

  const preferences = useQuery(api.userPreferences.queries.getUserPreferences, {});

  const sownStatus: Record<string, boolean> = {};
  seedTracking.forEach((s) => {
    sownStatus[s.plantId] = s.hasSown;
  });

  const seedRequirements = generateSeedRequirements(
    selectedGarden,
    plants,
    sownStatus,
    sowingConfigs,
    plantDefaults,
    preferences
  );

  const handleToggleSown = createHandleToggleSown(selectedGarden, toggleSeedSown);

  return (
    <View
      selectedGarden={selectedGarden}
      seedRequirements={seedRequirements}
      onToggleSown={handleToggleSown}
    />
  );
}
